/**
 * 배포 추적을 위한 localStorage 관리 유틸리티
 */

const TRACKING_KEY = 'deploymentTracking';
const STATUS_KEY = 'deploymentStatus';

export interface DeploymentTrackingData {
    trackingProducts: string[];
    productStatuses: Record<string, string>;
}

/**
 * localStorage에서 배포 추적 데이터를 가져옵니다
 */
export const getDeploymentTrackingData = (): DeploymentTrackingData => {
    try {
        const trackingProducts = JSON.parse(localStorage.getItem(TRACKING_KEY) || '[]');
        const productStatuses = JSON.parse(localStorage.getItem(STATUS_KEY) || '{}');

        return {
            trackingProducts: Array.isArray(trackingProducts) ? trackingProducts : [],
            productStatuses: typeof productStatuses === 'object' && productStatuses !== null ? productStatuses : {},
        };
    } catch (error) {
        console.warn('Failed to parse deployment tracking data from localStorage:', error);
        return {
            trackingProducts: [],
            productStatuses: {},
        };
    }
};

/**
 * 배포 추적을 시작합니다
 * @param productId 추적할 제품 ID
 * @param currentStatus 현재 제품 상태
 */
export const startDeploymentTracking = (productId: string, currentStatus: string): void => {
    try {
        const { trackingProducts, productStatuses } = getDeploymentTrackingData();

        // 이미 추적 중이 아닌 경우에만 추가
        if (!trackingProducts.includes(productId)) {
            trackingProducts.push(productId);
            localStorage.setItem(TRACKING_KEY, JSON.stringify(trackingProducts));
        }

        // 현재 상태 저장
        productStatuses[productId] = currentStatus;
        localStorage.setItem(STATUS_KEY, JSON.stringify(productStatuses));
    } catch (error) {
        console.warn('Failed to start deployment tracking:', error);
    }
};

/**
 * 배포 추적을 종료합니다
 * @param productId 추적을 종료할 제품 ID
 */
export const stopDeploymentTracking = (productId: string): void => {
    try {
        const { trackingProducts, productStatuses } = getDeploymentTrackingData();

        const updatedTrackingProducts = trackingProducts.filter(id => id !== productId);
        localStorage.setItem(TRACKING_KEY, JSON.stringify(updatedTrackingProducts));

        delete productStatuses[productId];

        if (Object.keys(productStatuses).length === 0) {
            localStorage.removeItem(STATUS_KEY);
        } else {
            localStorage.setItem(STATUS_KEY, JSON.stringify(productStatuses));
        }
    } catch (error) {
        console.warn('Failed to stop deployment tracking:', error);
    }
};

/**
 * 배포 상태를 업데이트합니다
 * @param productId 제품 ID
 * @param newStatus 새로운 상태
 */
export const updateDeploymentStatus = (productId: string, newStatus: string): void => {
    try {
        const { productStatuses } = getDeploymentTrackingData();

        productStatuses[productId] = newStatus;
        localStorage.setItem(STATUS_KEY, JSON.stringify(productStatuses));
    } catch (error) {
        console.warn('Failed to update deployment status:', error);
    }
};

/**
 * 모든 배포 추적 데이터를 정리합니다
 */
export const clearDeploymentTracking = (): void => {
    try {
        localStorage.removeItem(TRACKING_KEY);
        localStorage.removeItem(STATUS_KEY);
    } catch (error) {
        console.warn('Failed to clear deployment tracking:', error);
    }
};

/**
 * 특정 제품이 추적 중인지 확인합니다
 * @param productId 제품 ID
 * @returns 추적 중이면 true
 */
export const isProductBeingTracked = (productId: string): boolean => {
    try {
        const { trackingProducts } = getDeploymentTrackingData();
        return trackingProducts.includes(productId);
    } catch (error) {
        console.warn('Failed to check if product is being tracked:', error);
        return false;
    }
};

/**
 * 제품의 저장된 상태를 가져옵니다
 * @param productId 제품 ID
 * @returns 저장된 상태 또는 undefined
 */
export const getSavedProductStatus = (productId: string): string | undefined => {
    try {
        const { productStatuses } = getDeploymentTrackingData();
        return productStatuses[productId];
    } catch (error) {
        console.warn('Failed to get saved product status:', error);
        return undefined;
    }
};
